/**
 * Admin Service (Simplified)
 *
 * Clean implementation using the simplified tracking tables:
 *   - lesson_progress: Track lesson start/completion with time
 *   - page_views: Track user journey through pages
 *   - user_events: Track interactions and clicks
 */

import { supabase } from '@/integrations/supabase/client';
import {
  AdminUser,
  UserDetails,
  AdminDashboardStats,
  UserFilters,
  ProfileUpdates,
  AdminAction,
  ExportData,
  AnalyticsEvent,
  ProgressRecord,
  TimelineActivity
} from '@/types/admin';

export class AdminService {
  /**
   * Check if current user is admin
   */
  async isCurrentUserAdmin(): Promise<boolean> {
    const { data: { user }, error } = await supabase.auth.getUser();

    if (error || !user) {
      return false;
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', user.id)
      .single();

    return profile?.is_admin === true;
  }

  // ==========================================================================
  // USER MANAGEMENT
  // ==========================================================================

  /**
   * Get all users with activity metrics
   */
  async getAllUsers(filters?: UserFilters): Promise<AdminUser[]> {
    if (!(await this.isCurrentUserAdmin())) {
      throw new Error('Access denied: Admin privileges required');
    }

    // Build profiles query
    let query = supabase.from('profiles').select('*');

    if (filters?.search) {
      query = query.or(`email.ilike.%${filters.search}%,first_name.ilike.%${filters.search}%,last_name.ilike.%${filters.search}%`);
    }

    if (filters?.status === 'admin') {
      query = query.eq('is_admin', true);
    }

    if (filters?.role) {
      query = query.eq('role', filters.role);
    }

    if (filters?.techComfort) {
      query = query.eq('tech_comfort', filters.techComfort);
    }

    const { data: profiles, error } = await query;

    if (error) {
      console.error('Failed to fetch users:', error);
      throw error;
    }

    if (!profiles || profiles.length === 0) {
      return [];
    }

    // Get user_ids for activity queries
    const userIds = profiles.map(p => p.user_id).filter(Boolean);

    if (userIds.length === 0) {
      return profiles.map(p => this.mapProfileToAdminUser(p, null, [], []));
    }

    // Fetch activity data from new simplified tables
    const [progressResult, eventsResult, pageViewsResult] = await Promise.all([
      supabase
        .from('lesson_progress')
        .select('user_id, lesson_id, status, progress_percent, time_spent_seconds, updated_at')
        .in('user_id', userIds),
      supabase
        .from('user_events')
        .select('user_id, created_at')
        .in('user_id', userIds),
      supabase
        .from('page_views')
        .select('user_id, created_at')
        .in('user_id', userIds)
    ]);

    // Group data by user
    const progressByUser = this.groupBy(progressResult.data || [], 'user_id');
    const eventsByUser = this.groupBy(eventsResult.data || [], 'user_id');
    const pageViewsByUser = this.groupBy(pageViewsResult.data || [], 'user_id');

    // Map profiles to AdminUser
    let users = profiles.map(profile => {
      const progress = progressByUser.get(profile.user_id) || [];
      const events = eventsByUser.get(profile.user_id) || [];
      const pageViews = pageViewsByUser.get(profile.user_id) || [];
      return this.mapProfileToAdminUser(profile, progress, events, pageViews);
    });

    // Apply status filter
    if (filters?.status && filters.status !== 'all' && filters.status !== 'admin') {
      users = users.filter(u => {
        if (filters.status === 'active') {
          return u.activityStatus === 'active' || u.activityStatus === 'highly_active';
        }
        return u.activityStatus === filters.status;
      });
    }

    // Apply sorting
    users = this.sortUsers(users, filters?.sortBy, filters?.sortOrder);

    // Apply pagination
    const page = filters?.page || 1;
    const limit = filters?.limit || 20;
    const start = (page - 1) * limit;

    return users.slice(start, start + limit);
  }

  /**
   * Get detailed user information
   */
  async getUserDetails(userId: string): Promise<UserDetails> {
    if (!(await this.isCurrentUserAdmin())) {
      throw new Error('Access denied: Admin privileges required');
    }

    // Get profile (handle both id and user_id)
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .or(`id.eq.${userId},user_id.eq.${userId}`)
      .single();

    if (profileError) {
      throw profileError;
    }

    const actualUserId = profile.user_id;

    // Fetch all activity data
    const [progressResult, eventsResult, pageViewsResult, quizzesResult] = await Promise.all([
      supabase
        .from('lesson_progress')
        .select('*')
        .eq('user_id', actualUserId)
        .order('updated_at', { ascending: false }),
      supabase
        .from('user_events')
        .select('*')
        .eq('user_id', actualUserId)
        .order('created_at', { ascending: false })
        .limit(50),
      supabase
        .from('page_views')
        .select('*')
        .eq('user_id', actualUserId)
        .order('created_at', { ascending: false })
        .limit(50),
      supabase
        .from('quiz_responses')
        .select('*')
        .eq('user_id', actualUserId)
        .order('created_at', { ascending: false })
    ]);

    const progress = progressResult.data || [];
    const events = eventsResult.data || [];
    const pageViews = pageViewsResult.data || [];
    const quizzes = quizzesResult.data || [];

    // Map events
    const recentEvents: AnalyticsEvent[] = events.map(e => ({
      id: e.id,
      eventType: e.event_type,
      eventName: e.event_name,
      pagePath: e.page_path,
      metadata: e.event_data,
      createdAt: new Date(e.created_at)
    }));

    // Map progress
    const recentProgress: ProgressRecord[] = progress.map(p => ({
      id: p.id,
      contentType: 'lesson',
      contentId: p.lesson_id,
      status: p.status === 'completed' ? 'completed' : 'in_progress',
      progressPercentage: p.progress_percent || 0,
      timeSpentSeconds: p.time_spent_seconds || 0,
      firstAccessedAt: p.started_at ? new Date(p.started_at) : undefined,
      lastAccessedAt: new Date(p.updated_at || p.created_at),
      completedAt: p.completed_at ? new Date(p.completed_at) : undefined,
      progressData: p.progress_data
    }));

    // Build activity timeline
    const timeline: TimelineActivity[] = [
      ...recentEvents.map(e => ({
        type: 'event' as const,
        subtype: e.eventType,
        description: e.eventName || e.eventType,
        timestamp: e.createdAt,
        metadata: e.metadata
      })),
      ...recentProgress.map(p => ({
        type: 'progress' as const,
        subtype: p.contentType,
        description: `${p.contentId} (${p.status})`,
        timestamp: p.lastAccessedAt
      })),
      ...pageViews.map(pv => ({
        type: 'event' as const,
        subtype: 'page_view',
        description: `Viewed ${pv.page_path}`,
        timestamp: new Date(pv.created_at)
      }))
    ].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    // Calculate metrics
    const lessonsCompleted = progress.filter(p => p.status === 'completed').length;
    const totalTimeSpent = progress.reduce((sum, p) => sum + (p.time_spent_seconds || 0), 0);
    const totalEvents = events.length + pageViews.length;

    const lastActive = timeline[0]?.timestamp;
    const daysSince = lastActive ? (Date.now() - lastActive.getTime()) / (1000 * 60 * 60 * 24) : 999;
    const activityStatus: AdminUser['activityStatus'] =
      daysSince < 1 ? 'highly_active' :
      daysSince < 7 ? 'active' :
      daysSince < 30 ? 'inactive' : 'inactive';

    return {
      userId: profile.id,
      email: profile.email,
      firstName: profile.first_name,
      lastName: profile.last_name,
      role: profile.role,
      techComfort: profile.tech_comfort,
      aiExperience: profile.ai_experience,
      learningStyle: profile.learning_style,
      organizationName: profile.organization_name,
      organizationType: profile.organization_type,
      organizationSize: profile.organization_size,
      jobTitle: profile.job_title,
      location: profile.location,
      phoneNumber: profile.phone_number,
      yearsExperience: profile.years_experience,
      howDidYouHear: profile.how_did_you_hear,
      isAdmin: profile.is_admin || false,
      profileCompleted: profile.profile_completed || false,
      onboardingStep: profile.onboarding_step,
      firstChapterStarted: profile.first_chapter_started || false,
      firstChapterCompleted: profile.first_chapter_completed || false,
      createdAt: new Date(profile.created_at),
      lastActive,
      totalEvents,
      totalProgress: progress.length,
      lessonsCompleted,
      totalTimeSpent,
      activityStatus,
      recentEvents,
      recentProgress,
      quizResponses: quizzes || [],
      activityTimeline: timeline
    };
  }

  // ==========================================================================
  // DASHBOARD STATS
  // ==========================================================================

  /**
   * Get admin dashboard statistics
   */
  async getDashboardStats(): Promise<AdminDashboardStats> {
    if (!(await this.isCurrentUserAdmin())) {
      throw new Error('Access denied: Admin privileges required');
    }

    const now = new Date();
    const oneDayMs = 24 * 60 * 60 * 1000;
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const weekAgo = new Date(now.getTime() - oneDayMs * 7);
    const monthAgo = new Date(now.getTime() - oneDayMs * 30);
    const twoWeeksAgo = new Date(now.getTime() - oneDayMs * 14);

    // Get total users
    const { count: totalUsers } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true });

    // Get new users (last 7 days)
    const { count: newUsers } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true })
      .gte('created_at', weekAgo.toISOString());

    // Get active users from page_views and user_events
    const [
      { data: todayActivity },
      { data: weekActivity },
      { data: monthActivity }
    ] = await Promise.all([
      supabase.rpc('get_active_users_since', { since_date: startOfToday.toISOString() }),
      supabase.rpc('get_active_users_since', { since_date: weekAgo.toISOString() }),
      supabase.rpc('get_active_users_since', { since_date: monthAgo.toISOString() })
    ]).catch(() => [{ data: null }, { data: null }, { data: null }]);

    // Fallback: Query directly if RPC doesn't exist
    let activeToday = todayActivity?.count || 0;
    let activeThisWeek = weekActivity?.count || 0;
    let activeThisMonth = monthActivity?.count || 0;

    if (!todayActivity) {
      const [todayViews, todayEvents] = await Promise.all([
        supabase
          .from('page_views')
          .select('user_id')
          .gte('created_at', startOfToday.toISOString()),
        supabase
          .from('user_events')
          .select('user_id')
          .gte('created_at', startOfToday.toISOString())
      ]);

      const todayUserIds = new Set([
        ...(todayViews.data || []).map(r => r.user_id),
        ...(todayEvents.data || []).map(r => r.user_id)
      ]);
      activeToday = todayUserIds.size;
    }

    if (!weekActivity) {
      const [weekViews, weekEvents] = await Promise.all([
        supabase
          .from('page_views')
          .select('user_id')
          .gte('created_at', weekAgo.toISOString()),
        supabase
          .from('user_events')
          .select('user_id')
          .gte('created_at', weekAgo.toISOString())
      ]);

      const weekUserIds = new Set([
        ...(weekViews.data || []).map(r => r.user_id),
        ...(weekEvents.data || []).map(r => r.user_id)
      ]);
      activeThisWeek = weekUserIds.size;
    }

    if (!monthActivity) {
      const [monthViews, monthEvents] = await Promise.all([
        supabase
          .from('page_views')
          .select('user_id')
          .gte('created_at', monthAgo.toISOString()),
        supabase
          .from('user_events')
          .select('user_id')
          .gte('created_at', monthAgo.toISOString())
      ]);

      const monthUserIds = new Set([
        ...(monthViews.data || []).map(r => r.user_id),
        ...(monthEvents.data || []).map(r => r.user_id)
      ]);
      activeThisMonth = monthUserIds.size;
    }

    // Get lesson stats
    const { data: lessonStats } = await supabase
      .from('lesson_progress')
      .select('status, time_spent_seconds');

    const lessonsCompleted = (lessonStats || []).filter(l => l.status === 'completed').length;
    const totalTimeSpent = (lessonStats || []).reduce((sum, l) => sum + (l.time_spent_seconds || 0), 0);

    // Get total events
    const { count: totalEvents } = await supabase
      .from('user_events')
      .select('*', { count: 'exact', head: true });

    // Calculate at-risk users (active 14-30 days ago but not recently)
    const { data: recentlyActive } = await supabase
      .from('page_views')
      .select('user_id')
      .gte('created_at', twoWeeksAgo.toISOString());

    const { data: previouslyActive } = await supabase
      .from('page_views')
      .select('user_id')
      .gte('created_at', monthAgo.toISOString())
      .lt('created_at', twoWeeksAgo.toISOString());

    const recentUserIds = new Set((recentlyActive || []).map(r => r.user_id));
    const atRiskUsers = (previouslyActive || [])
      .filter(r => !recentUserIds.has(r.user_id))
      .length;

    const total = totalUsers || 0;

    return {
      totalUsers: total,
      activeToday,
      activeThisWeek,
      activeThisMonth,
      totalLessonsCompleted: lessonsCompleted,
      totalEvents: totalEvents || 0,
      engagementRate: total > 0 ? Math.round((activeThisWeek / total) * 100) : 0,
      totalTimeSpentSeconds: totalTimeSpent,
      newUsers: newUsers || 0,
      highlyActiveUsers: activeToday,
      atRiskUsers
    };
  }

  // ==========================================================================
  // USER JOURNEY ANALYTICS
  // ==========================================================================

  /**
   * Get user journey (page flow) for a specific user
   */
  async getUserJourney(userId: string, limit = 100): Promise<{
    pagePath: string;
    pageTitle?: string;
    timeOnPage?: number;
    createdAt: Date;
  }[]> {
    if (!(await this.isCurrentUserAdmin())) {
      throw new Error('Access denied: Admin privileges required');
    }

    const { data, error } = await supabase
      .from('page_views')
      .select('page_path, page_title, time_on_page_seconds, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return (data || []).map(pv => ({
      pagePath: pv.page_path,
      pageTitle: pv.page_title,
      timeOnPage: pv.time_on_page_seconds,
      createdAt: new Date(pv.created_at)
    }));
  }

  /**
   * Get lesson completion rates
   */
  async getLessonCompletionRates(): Promise<{
    lessonId: string;
    totalStarted: number;
    totalCompleted: number;
    completionRate: number;
    avgTimeSeconds: number;
  }[]> {
    if (!(await this.isCurrentUserAdmin())) {
      throw new Error('Access denied: Admin privileges required');
    }

    const { data, error } = await supabase
      .from('lesson_progress')
      .select('lesson_id, status, time_spent_seconds');

    if (error) throw error;

    // Group by lesson_id
    const lessonMap = new Map<string, { started: number; completed: number; totalTime: number }>();

    (data || []).forEach(record => {
      const existing = lessonMap.get(record.lesson_id) || { started: 0, completed: 0, totalTime: 0 };
      existing.started++;
      if (record.status === 'completed') {
        existing.completed++;
      }
      existing.totalTime += record.time_spent_seconds || 0;
      lessonMap.set(record.lesson_id, existing);
    });

    return Array.from(lessonMap.entries()).map(([lessonId, stats]) => ({
      lessonId,
      totalStarted: stats.started,
      totalCompleted: stats.completed,
      completionRate: stats.started > 0 ? Math.round((stats.completed / stats.started) * 100) : 0,
      avgTimeSeconds: stats.started > 0 ? Math.round(stats.totalTime / stats.started) : 0
    })).sort((a, b) => a.lessonId.localeCompare(b.lessonId));
  }

  /**
   * Get drop-off analysis
   */
  async getDropoffAnalysis(): Promise<{
    lessonId: string;
    usersStarted: number;
    usersDropped: number;
    dropoffRate: number;
  }[]> {
    if (!(await this.isCurrentUserAdmin())) {
      throw new Error('Access denied: Admin privileges required');
    }

    const { data, error } = await supabase
      .from('lesson_progress')
      .select('lesson_id, status');

    if (error) throw error;

    // Group by lesson_id
    const lessonMap = new Map<string, { started: number; completed: number }>();

    (data || []).forEach(record => {
      const existing = lessonMap.get(record.lesson_id) || { started: 0, completed: 0 };
      existing.started++;
      if (record.status === 'completed') {
        existing.completed++;
      }
      lessonMap.set(record.lesson_id, existing);
    });

    return Array.from(lessonMap.entries())
      .map(([lessonId, stats]) => ({
        lessonId,
        usersStarted: stats.started,
        usersDropped: stats.started - stats.completed,
        dropoffRate: stats.started > 0
          ? Math.round(((stats.started - stats.completed) / stats.started) * 100)
          : 0
      }))
      .sort((a, b) => b.dropoffRate - a.dropoffRate);
  }

  // ==========================================================================
  // ADMIN ACTIONS
  // ==========================================================================

  /**
   * Fetch recent admin actions for audit visibility
   */
  async getRecentAdminActions(limit = 25): Promise<AdminAction[]> {
    if (!(await this.isCurrentUserAdmin())) {
      throw new Error('Access denied: Admin privileges required');
    }

    const { data, error } = await supabase
      .from('admin_actions')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      // Table might not exist
      if (error.code === '42P01' || /does not exist/i.test(error.message || '')) {
        return [];
      }
      throw error;
    }

    return (data || []).map(action => ({
      id: action.id,
      adminUserId: action.admin_user_id,
      targetUserId: action.target_user_id || undefined,
      actionType: action.action_type,
      actionDetails: action.action_details || {},
      ipAddress: action.ip_address || undefined,
      userAgent: action.user_agent || undefined,
      createdAt: new Date(action.created_at)
    }));
  }

  /**
   * Update user profile
   */
  async updateUserProfile(userId: string, updates: ProfileUpdates): Promise<void> {
    if (!(await this.isCurrentUserAdmin())) {
      throw new Error('Access denied: Admin privileges required');
    }

    const { error } = await supabase
      .from('profiles')
      .update({
        first_name: updates.firstName,
        last_name: updates.lastName,
        tech_comfort: updates.techComfort,
        ai_experience: updates.aiExperience,
        learning_style: updates.learningStyle,
        job_title: updates.jobTitle,
        organization_name: updates.organizationName,
        organization_type: updates.organizationType,
        organization_size: updates.organizationSize,
        location: updates.location,
        phone_number: updates.phoneNumber,
        is_admin: updates.isAdmin
      })
      .eq('id', userId);

    if (error) {
      throw error;
    }
  }

  /**
   * Export user data
   */
  async exportUserData(userId: string): Promise<ExportData> {
    const details = await this.getUserDetails(userId);
    const { data: { user } } = await supabase.auth.getUser();

    return {
      profile: details,
      events: details.recentEvents,
      progress: details.recentProgress,
      quizzes: details.quizResponses,
      exportedAt: new Date(),
      exportedBy: user?.email || 'unknown'
    };
  }

  // ==========================================================================
  // HELPERS
  // ==========================================================================

  private groupBy<T>(items: T[], key: keyof T): Map<string, T[]> {
    const map = new Map<string, T[]>();
    items.forEach(item => {
      const k = String(item[key]);
      const existing = map.get(k) || [];
      existing.push(item);
      map.set(k, existing);
    });
    return map;
  }

  private mapProfileToAdminUser(
    profile: any,
    progress: any[] | null,
    events: any[],
    pageViews: any[]
  ): AdminUser {
    const progressArr = progress || [];
    const now = Date.now();
    const oneDayMs = 24 * 60 * 60 * 1000;

    // Calculate metrics
    const lessonsCompleted = progressArr.filter(p => p.status === 'completed').length;
    const totalTimeSpent = progressArr.reduce((sum, p) => sum + (p.time_spent_seconds || 0), 0);
    const totalEvents = events.length + pageViews.length;

    // Find last active timestamp
    const timestamps = [
      ...progressArr.map(p => p.updated_at ? new Date(p.updated_at).getTime() : 0),
      ...events.map(e => e.created_at ? new Date(e.created_at).getTime() : 0),
      ...pageViews.map(pv => pv.created_at ? new Date(pv.created_at).getTime() : 0)
    ].filter(t => t > 0);

    const lastActiveTime = timestamps.length > 0 ? Math.max(...timestamps) : 0;
    const lastActive = lastActiveTime > 0 ? new Date(lastActiveTime) : undefined;

    // Determine activity status
    let activityStatus: AdminUser['activityStatus'] = 'inactive';
    if (lastActive) {
      const daysSince = (now - lastActive.getTime()) / oneDayMs;
      if (daysSince < 1) activityStatus = 'highly_active';
      else if (daysSince < 7) activityStatus = 'active';
      else if (daysSince < 14) activityStatus = 'inactive';
      else if (daysSince < 30) activityStatus = 'at_risk';
      else activityStatus = 'inactive';
    } else {
      const createdAt = profile.created_at ? new Date(profile.created_at) : undefined;
      if (createdAt && (now - createdAt.getTime()) < oneDayMs * 7) {
        activityStatus = 'new';
      } else {
        activityStatus = 'at_risk';
      }
    }

    return {
      userId: profile.id,
      email: profile.email,
      firstName: profile.first_name,
      lastName: profile.last_name,
      role: profile.role,
      techComfort: profile.tech_comfort,
      aiExperience: profile.ai_experience,
      learningStyle: profile.learning_style,
      organizationName: profile.organization_name,
      organizationType: profile.organization_type,
      isAdmin: profile.is_admin || false,
      profileCompleted: profile.profile_completed || false,
      createdAt: profile.created_at ? new Date(profile.created_at) : new Date(0),
      lastActive,
      totalEvents,
      totalProgress: progressArr.length,
      lessonsCompleted,
      totalTimeSpent,
      activityStatus
    };
  }

  private sortUsers(
    users: AdminUser[],
    sortBy?: string,
    sortOrder?: 'asc' | 'desc'
  ): AdminUser[] {
    if (!sortBy) {
      // Default: sort by last active descending
      return users.sort((a, b) => {
        const aTime = a.lastActive?.getTime() || 0;
        const bTime = b.lastActive?.getTime() || 0;
        return bTime - aTime;
      });
    }

    return users.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortBy) {
        case 'email':
          aVal = a.email;
          bVal = b.email;
          break;
        case 'name':
          aVal = [a.firstName, a.lastName].filter(Boolean).join(' ');
          bVal = [b.firstName, b.lastName].filter(Boolean).join(' ');
          break;
        case 'lastActive':
          aVal = a.lastActive?.getTime() || 0;
          bVal = b.lastActive?.getTime() || 0;
          break;
        case 'events':
          aVal = a.totalEvents;
          bVal = b.totalEvents;
          break;
        case 'progress':
          aVal = a.lessonsCompleted;
          bVal = b.lessonsCompleted;
          break;
        default:
          return 0;
      }

      if (sortOrder === 'desc') {
        return bVal > aVal ? 1 : -1;
      }
      return aVal > bVal ? 1 : -1;
    });
  }
}

// Export singleton instance
export const adminService = new AdminService();
